import * as t from 'io-ts';

import {RAccess} from '../../models/api/accessModel';
import {RAccount} from '../../models/api/accountModel';
import {RActivity} from '../../models/api/activityModel';
import {RAnswerFolder} from '../../models/api/answerFolderModel';
import {RAnswer} from '../../models/api/answerModel';
import {RApiToken} from '../../models/api/apiTokenModel';
import {
  makeEnumRuntimeType,
  RCommentUidRef,
  RLazyTeammateIdRef,
  RMessageUidRef,
} from '../../models/api/apiTypes';
import {RApplication} from '../../models/api/applicationModel';
import {AppLanguagesEnum} from '../../models/api/bootModel';
import {RCalendarEvent} from '../../models/api/calendarEventModel';
import {RCalendarEventVersion} from '../../models/api/calendarEventVersionModel';
import {RCalendar} from '../../models/api/calendarModel';
import {RCalendarVersion} from '../../models/api/calendarVersionModel';
import {RCardGroup} from '../../models/api/cardGroupModel';
import {RCard} from '../../models/api/cardModel';
import {RCardNote} from '../../models/api/cardNoteModel';
import {RChannel} from '../../models/api/channelModel';
import {RComment} from '../../models/api/commentModel';
import {RCompany} from '../../models/api/companyModel';
import {RContact} from '../../models/api/contactModel';
import {RConversation} from '../../models/api/conversationModel';
import {RCounters} from '../../models/api/countersModel';
import {RCrmSync} from '../../models/api/crmSyncModel';
import {RCustomEmojis} from '../../models/api/customEmojiModel';
import {RCustomField} from '../../models/api/customFieldModel';
import {RCustomView} from '../../models/api/customViewModel';
import {RDelegation} from '../../models/api/delegationModel';
import {RExternalChannelType} from '../../models/api/externalChannelTypeModel';
import {RHelpChatConfig} from '../../models/api/helpChatConfigModel';
import {RInbox} from '../../models/api/inboxModel';
import {RIntegrationError} from '../../models/api/integrationErrorModel';
import {RIntegration} from '../../models/api/integrationModel';
import {RIntegrationType} from '../../models/api/integrationTypesModel';
import {RJob} from '../../models/api/jobModel';
import {RLaunchdarklyConfig} from '../../models/api/launchdarklyConfigModel';
import {RLegacyApplication} from '../../models/api/legacyApplicationModel';
import {RLens} from '../../models/api/lensModel';
import {RLocalApplication} from '../../models/api/localApplicationModel';
import {RLocalPreferences} from '../../models/api/localPreferencesModel';
import {RMessage} from '../../models/api/messageModel';
import {RPlugin} from '../../models/api/pluginModel';
import {RPrecog} from '../../models/api/precogModel';
import {RPreferences} from '../../models/api/preferencesModel';
import {RCPushConfig, RPushConfig} from '../../models/api/pushConfigModel';
import {RRole} from '../../models/api/roleModel';
import {RSchedulingLink} from '../../models/api/schedulingLinkModel';
import {RSignature} from '../../models/api/signatureModel';
import {RTag} from '../../models/api/tagModel';
import {RLazyTeammate, RTeammate} from '../../models/api/teammateModel';
import {RTeam} from '../../models/api/teamModel';
import {RTopic} from '../../models/api/topicModel';
import {RUserMetadata} from '../../models/api/userMetadataModel';
import {RVacationResponder} from '../../models/api/vacationResponderModel';
import {RWorkspace} from '../../models/api/workspaceModel';
import {ROnlineTeammate} from '../../models/push/onlineTeammateModel';
import {
  makePermanentLazyResourceTable,
  makePermanentResourceMap,
  makePermanentResourceTable,
  makeResourceMap,
  makeTransientResourceMap,
  makeTransientResourceTable,
} from '../appTypes';
import {
  getShouldPersistChannel,
  getShouldPersistConversation,
  getShouldPersistInbox,
} from './persistSelectors';

/*
 * Runtime type.
 */

export const RConfigState = t.readonly(
  t.type({
    host: t.string,
    buildVersion: t.string,
    push: RPushConfig,
    chatPush: t.union([t.undefined, RCPushConfig]),
    helpChat: t.union([t.undefined, RHelpChatConfig]),
    launchdarkly: RLaunchdarklyConfig,
    isImpersonating: t.boolean,
    hasTrialRestarted: t.boolean,
    allowedDomainForEmbed: t.union([t.undefined, t.string]),
    language: makeEnumRuntimeType(AppLanguagesEnum),
  }),
  'config',
);

export const RDataState = t.readonly(
  t.type({
    // Permanent.
    config: RConfigState,
    counters: RCounters,
    access: RAccess,
    preferences: RPreferences,
    userMetadata: RUserMetadata,
    company: RCompany,
    userId: RLazyTeammateIdRef,
    bootFetchedAt: t.Integer,
    teams: makePermanentResourceMap(t.Integer, RTeam),
    teammates: makePermanentResourceMap(t.Integer, RTeammate),
    delegations: makePermanentResourceMap(t.Integer, RDelegation),
    answers: makePermanentLazyResourceTable(t.Integer, RAnswer),
    answerFolders: makePermanentLazyResourceTable(t.Integer, RAnswerFolder),
    crmSyncs: makePermanentLazyResourceTable(t.Integer, RCrmSync),
    signatures: makePermanentLazyResourceTable(t.Integer, RSignature),
    integrations: makePermanentResourceMap(t.Integer, RIntegration),
    integrationTypes: makePermanentLazyResourceTable(t.string, RIntegrationType),
    plugins: makeResourceMap(t.Integer, RPlugin),
    legacyApplications: makePermanentResourceMap(t.Integer, RLegacyApplication),
    customViews: makePermanentResourceMap(t.Integer, RCustomView),
    customFields: makePermanentResourceMap(t.Integer, RCustomField),
    calendars: makePermanentResourceMap(t.Integer, RCalendar),
    calendarVersions: makePermanentResourceMap(t.Integer, RCalendarVersion),
    roles: makeResourceMap(t.string, RRole),
    jobs: makeResourceMap(t.Integer, RJob),
    schedulingLinks: makePermanentResourceTable(t.Integer, RSchedulingLink),
    vacationResponders: makeResourceMap(t.Integer, RVacationResponder),
    externalChannelTypes: makePermanentLazyResourceTable(t.Integer, RExternalChannelType),
    applications: makePermanentLazyResourceTable(t.Integer, RApplication),
    lenses: makePermanentResourceMap(t.Integer, RLens),

    // Permanent, client-side.
    localPreferences: RLocalPreferences,
    onlineTeammates: t.union([t.undefined, t.readonlyArray(ROnlineTeammate)]),
    chatPrecogs: makeResourceMap(t.Integer, t.boolean),
    chatUsersOnline: makeResourceMap(t.Integer, t.boolean),
    conversationPrecogs: makeResourceMap(t.Integer, t.readonlyArray(RPrecog)),
    offlineDraftMessageUids: t.readonlyArray(RMessageUidRef),
    offlineDraftCommentUids: t.readonlyArray(RCommentUidRef),
    integrationErrors: makePermanentResourceMap(t.Integer, RIntegrationError),
    customEmojis: RCustomEmojis,
    workspace: RWorkspace,
    // TODO: Make these transient.
    calendarEvents: makePermanentResourceMap(t.string, RCalendarEvent),
    calendarEventVersions: makePermanentResourceMap(t.string, RCalendarEventVersion),

    // Transient.
    contacts: makeTransientResourceMap(t.string, RContact),
    cards: makeTransientResourceMap(t.Integer, RCard),
    cardGroups: makeTransientResourceMap(t.Integer, RCardGroup),
    cardNotes: makeTransientResourceMap(t.Integer, RCardNote),
    lazyTeammates: makeTransientResourceMap(t.Integer, RLazyTeammate),
    inboxes: makeTransientResourceTable(t.Integer, RInbox, getShouldPersistInbox),
    channels: makeTransientResourceTable(t.Integer, RChannel, getShouldPersistChannel),
    conversations: makeTransientResourceTable(t.Integer, RConversation, getShouldPersistConversation),
    tags: makeTransientResourceMap(t.Integer, RTag),
    activities: makeTransientResourceMap(t.Integer, RActivity),
    messages: makeTransientResourceMap(t.string, RMessage),
    comments: makeTransientResourceTable(t.string, RComment),
    accounts: makeTransientResourceMap(t.string, RAccount),
    topics: makeTransientResourceMap(t.number, RTopic),
    localApplications: makeTransientResourceMap(t.number, RLocalApplication),
    apiTokens: makeTransientResourceMap(t.number, RApiToken),
  }),
);

/*
 * Types.
 */

export interface ConfigState extends t.TypeOf<typeof RConfigState> {}
export interface DataState extends t.TypeOf<typeof RDataState> {}

/** Represents all data state that cannot be reconstructed from another source. */
export type DataStateForBoot = Omit<
  DataState,
  | 'chatPrecogs'
  | 'chatUsersOnline'
  | 'localPreferences'
  | 'onlineTeammates'
  | 'conversationPrecogs'
  | 'offlineDraftMessageUids'
  | 'offlineDraftCommentUids'
  | 'integrationErrors'
  | 'schedulingLinks'
  | 'customEmojis'
  | 'lazyTeammates'
  | 'crmSyncs'
  | 'externalChannelTypes'
  | 'integrationTypes'
  | 'applications'
>;
